//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { Loop } from './components/Sidebar/SidebarTypes';
import { LoopLinksResponse } from './components/Loops/response';
import { useTeamspaceContext } from './TeamspaceContext';
import { useUserAuth } from './UserAuth';
import LoopDataService from './utils/LoopDataService';

export type failedLoop = {
  id?: string | null;
  name?: string;
  mode?: string;
  teamSpaceName?: string;
  incomplete?: boolean;
  fullRetry?: boolean;
};

export type incompleteLoopType = {
  createdTime: string;
  id: string;
  name?: string;
  status?: string;
  displayName: string;
  description?: string;
  tenantId: string;
  teamSpaceId: string;
  workflowType: string;
  enabled: boolean;
  associatedUserIds: string[];
  failedTask?: {
    status: string;
    executionResult: string;
    creationDate: string;
    heartbeat: string;
    updateCount: number;
    completionDate: string;
    type: string;
    id: string;
  };
  _links: {
    self: {
      href: string;
    };
    loopEntity: {
      href: string;
    };
    modify: {
      href: string;
      type: string;
    };
    disable: {
      href: string;
      type: string;
    };
    users: {
      href: string;
    };
    tasks?: {
      href: string;
    };
  };
};

type LoopContextType = {
  failedLoops: failedLoop[];
  setFailedLoops: (loops: failedLoop[]) => void;
  retryLoop: failedLoop | null;
  setRetryLoop: (loop: failedLoop | null) => void;
  incompleteLoops: incompleteLoopType[];
  setIncompleteLoops: (loops: incompleteLoopType[]) => void;
  completedLoops: Loop[];
  setCompletedLoops: (loops: Loop[]) => void;
  incompleteLoop: incompleteLoopType | undefined;
  setIncompleteLoop: (loop: incompleteLoopType | undefined) => void;
  createNewLoop: boolean;
  setCreateNewLoop: (createLoop: boolean) => void;
  selectedLoop: { id: string; name: string } | null;
  setSelectedLoop: (loop: { id: string; name: string } | null) => void;
  firstLoopData: incompleteLoopType | null;
  setFirstLoopData: (loop: incompleteLoopType | null) => void;
  firstLoopId: string;
  loopLinksResponseInContext: LoopLinksResponse | undefined;
  setLoopLinksResponseInContext: (
    response: LoopLinksResponse | undefined,
  ) => void;
};

const LoopContext = createContext<LoopContextType | undefined>(undefined);

export const useLoopContext = () => {
  const context = useContext(LoopContext);
  if (context === undefined) {
    throw new Error('useLoopContext must be used within a LoopProvider');
  }
  return context;
};

type LoopProviderProps = {
  children: ReactNode;
};

export const LoopProvider = ({ children }: LoopProviderProps) => {
  const [failedLoops, setFailedLoops] = useState<failedLoop[]>([]);
  const [retryLoop, setRetryLoop] = useState<failedLoop | null>(null);
  const [incompleteLoops, setIncompleteLoops] = useState<incompleteLoopType[]>(
    [],
  );
  const [completedLoops, setCompletedLoops] = useState<Loop[]>([]);
  const [incompleteLoop, setIncompleteLoop] = useState<incompleteLoopType>();
  const [createNewLoop, setCreateNewLoop] = useState<boolean>(false);
  const [selectedLoop, setSelectedLoop] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [firstLoopData, setFirstLoopData] = useState<incompleteLoopType | null>(
    null,
  );
  const [loopLinksResponseInContext, setLoopLinksResponseInContext] =
    useState<LoopLinksResponse>();

  const { teamspaceId } = useTeamspaceContext();
  const { user, isAuthenticated } = useUserAuth();

  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  useEffect(() => {
    const fetchCompletedLoops = async () => {
      if (!teamspaceId || !isAuthenticated) return;

      try {
        const response = await loopDataService.getLoopsAll(teamspaceId);
        const loops: Loop[] = response?._embedded?.loops || [];
        const completedEnabledLoops = loops.filter(
          (loop) => loop.enabled && loop.completed,
        );
        setCompletedLoops(completedEnabledLoops);
      } catch (error) {
        console.error('Failed to fetch completed loops:', error);
        setCompletedLoops([]);
      }
    };

    fetchCompletedLoops();
  }, [teamspaceId, isAuthenticated, loopDataService]);

  return (
    <LoopContext.Provider
      value={{
        failedLoops,
        setFailedLoops,
        retryLoop,
        setRetryLoop,
        incompleteLoops,
        setIncompleteLoops,
        completedLoops,
        setCompletedLoops,
        incompleteLoop,
        setIncompleteLoop,
        createNewLoop,
        setCreateNewLoop,
        selectedLoop,
        setSelectedLoop,
        firstLoopData,
        setFirstLoopData,
        firstLoopId: firstLoopData?.id || '',
        loopLinksResponseInContext,
        setLoopLinksResponseInContext,
      }}
    >
      {children}
    </LoopContext.Provider>
  );
};
